/* eslint-disable no-console */
/**
 * Contrôle de version de l'application (« version gate »).
 *
 * PRINCIPE
 * --------
 *   version.json (RÉSEAU, no-store)  = ce qui est DÉPLOYÉ sur le serveur
 *   process.env.VUE_APP_V_SW (BUNDLE) = ce qui TOURNE réellement sur le poste
 *
 * La divergence des deux est le seul signal fiable de mise à jour disponible.
 * Comparer la constante compilée à une valeur stockée localement ne marcherait
 * pas : les deux viennent du même bundle périmé et seront toujours d'accord.
 * C'est précisément la panne de production (cf. scripts/generate-version.js).
 *
 * IndexedDB ne sert donc PAS d'arbitre. Elle mémorise l'historique : version
 * installée, version visée, nombre de tentatives. Ce compteur permet de
 * détecter une mise à jour qui échoue (service worker bloqué) et d'escalader.
 *
 * Ce module ne dépend ni de Vue ni du store : il doit rester opérationnel même
 * si le montage de l'application échoue.
 */

import { initDB } from '@/service/indexedDBService';
import { urlAvecParamMaj } from '@/service/appVersionUrl';

// Version compilée dans CE bundle. Injectée par vue.config.js depuis
// package.json (DefinePlugin → process.env.HSIG_APP_VERSION).
//
// NE PAS remplacer par VUE_APP_V_SW : vue-cli injecte automatiquement toute
// variable VUE_APP_* du .env par-dessus le DefinePlugin, et la valeur du .env
// traîne un commentaire `//` que dotenv ne sait pas retirer. La comparaison
// serait alors faussée en permanence.
const VERSION_BUNDLE = process.env.HSIG_APP_VERSION || '0.0.0';

const STORE = 'appVersion';
const CLE = 'app';
const CLE_LOCALSTORAGE = 'hsig-app-version';

const DELAI_FETCH_MS = 8000;
const INTERVALLE_VERIF_MS = 30 * 60 * 1000;   // 30 min
const DELAI_MIN_REVERIF_MS = 5 * 60 * 1000;   // anti-rafale sur visibilitychange

let derniereVerif = 0;
let verificationEnCours = false;
let surveillanceDemarree = false;

// ─────────────────────────────────────────────────────────────────────────────
// Lecture de la version déployée (réseau)
// ─────────────────────────────────────────────────────────────────────────────

function urlVersionJson() {
    // BASE_URL (publicPath de vue-cli, "/" ici) et NON VUE_APP_URL : cette
    // dernière vaut "http://localhost:9598/" dans le .env de développement.
    // Un build lancé sans avoir basculé le .env produirait un bundle qui
    // interroge localhost chez le client - le contrôle de version serait
    // silencieusement mort, exactement la panne qu'il doit corriger.
    // version.json est servi à la racine du même domaine que l'application :
    // un chemin absolu est correct et insensible à la configuration.
    const base = process.env.BASE_URL || '/';
    return `${base}version.json`;
}

/**
 * Lit version.json sur le réseau. Renvoie null en cas d'échec (hors ligne,
 * 404, JSON invalide) : une mise à jour indéterminée ne doit JAMAIS bloquer
 * l'application.
 *
 * Triple protection contre le cache : cache:'no-store' (cache HTTP),
 * paramètre horodaté (proxies et service worker), en-tête no-cache.
 */
async function lireVersionDeployee() {
    const controleur = new AbortController();
    const minuteur = setTimeout(() => controleur.abort(), DELAI_FETCH_MS);

    try {
        const reponse = await fetch(`${urlVersionJson()}?t=${Date.now()}`, {
            method: 'GET',
            cache: 'no-store',
            credentials: 'omit',
            headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            signal: controleur.signal,
        });

        if (!reponse.ok) {
            console.warn(`[version] version.json indisponible (HTTP ${reponse.status})`);
            return null;
        }

        const data = await reponse.json();
        if (!data || typeof data.version !== 'string' || !data.version) {
            console.warn('[version] version.json illisible', data);
            return null;
        }
        return data;
    } catch (e) {
        // AbortError, panne réseau, réponse non-JSON : silence côté utilisateur.
        console.warn('[version] Lecture de version.json impossible', e && e.message);
        return null;
    } finally {
        clearTimeout(minuteur);
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Persistance - IndexedDB, avec repli localStorage
//
// Le repli n'est pas cosmétique : le contrôle de version doit survivre à une
// base corrompue ou indisponible, cas de figure fréquent chez les postes qui
// ont justement besoin d'être mis à jour. Aucune écriture ici n'est critique,
// toutes échouent en silence.
// ─────────────────────────────────────────────────────────────────────────────

async function lireEtat() {
    try {
        const db = await initDB();
        if (db.objectStoreNames.contains(STORE)) {
            const enr = await db.get(STORE, CLE);
            if (enr) return enr;
        }
    } catch (e) {
        console.warn('[version] Lecture IndexedDB impossible, repli localStorage', e && e.message);
    }

    try {
        const brut = window.localStorage.getItem(CLE_LOCALSTORAGE);
        if (brut) return JSON.parse(brut);
    } catch (e) { /* localStorage indisponible ou JSON cassé */ }

    return null;
}

async function ecrireEtat(patch) {
    const precedent = (await lireEtat()) || {};
    const etat = { ...precedent, ...patch, k: CLE, majLe: Date.now() };

    try {
        const db = await initDB();
        if (db.objectStoreNames.contains(STORE)) {
            await db.put(STORE, etat);
        }
    } catch (e) {
        console.warn('[version] Écriture IndexedDB impossible', e && e.message);
    }

    // Toujours doublé en localStorage : c'est le seul support encore lisible
    // si la base doit être purgée par l'auto-réparation du boot.
    try {
        window.localStorage.setItem(CLE_LOCALSTORAGE, JSON.stringify(etat));
    } catch (e) { /* quota ou mode privé */ }

    return etat;
}

// ─────────────────────────────────────────────────────────────────────────────
// Application de la mise à jour
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Désinscrit tous les service workers et vide TOUS les Cache Storage.
 *
 * IndexedDB n'est JAMAIS touchée : elle contient les commandes de caisse non
 * encore synchronisées et les commandes des serveurs en salle. Les détruire
 * pour installer une mise à jour serait une perte de chiffre d'affaires.
 */
async function purgerServiceWorkerEtCaches() {
    const taches = [];

    if ('serviceWorker' in navigator) {
        taches.push(
            navigator.serviceWorker.getRegistrations()
                .then(regs => Promise.all(regs.map(r => r.unregister())))
                .then(n => console.log(`[version] ${n.length} service worker(s) désinscrit(s)`))
                .catch(e => console.warn('[version] Désinscription SW échouée', e))
        );
    }

    if ('caches' in window) {
        taches.push(
            caches.keys()
                .then(noms => Promise.all(noms.map(n => caches.delete(n))))
                .then(r => console.log(`[version] ${r.length} cache(s) vidé(s)`))
                .catch(e => console.warn('[version] Purge des caches échouée', e))
        );
    }

    // Garde-fou : la purge ne doit jamais empêcher le rechargement. Si une
    // opération reste pendante au-delà de 5 s, on recharge quand même - le
    // navigateur terminera la désinscription de son côté.
    await Promise.race([
        Promise.all(taches),
        new Promise(resolve => setTimeout(resolve, 5000)),
    ]);
}

function rechargerSansCache(versionCible) {
    // replace() et non reload() : reload peut resservir la page depuis le cache
    // HTTP. Une URL jamais vue force une requête réseau. replace() évite en
    // outre d'empiler une entrée d'historique qui ramènerait à l'ancienne page.
    window.location.replace(urlAvecParamMaj(versionCible));
}

/**
 * Purge et recharge, SANS rien enregistrer.
 *
 * Utilisé par la récupération des ChunkLoadError, qui applique le même
 * traitement sans viser de version : y faire passer appliquerMiseAJour()
 * inscrirait une fausse version cible et ferait afficher un message d'échec de
 * mise à jour au démarrage suivant.
 *
 * Ne rend jamais la main : la page est remplacée.
 */
async function purgerEtRecharger(etiquette) {
    await purgerServiceWorkerEtCaches();
    rechargerSansCache(etiquette);
}

/**
 * Applique la mise à jour : compte la tentative, purge SW + caches, recharge.
 * Ne rend jamais la main (la page est remplacée).
 */
async function appliquerMiseAJour(versionCible) {
    const etat = (await lireEtat()) || {};

    // Le compteur ne se cumule que pour une MÊME version cible : viser une
    // nouvelle version repart de zéro.
    const tentatives = etat.versionCible === versionCible
        ? (etat.tentatives || 0) + 1
        : 1;

    await ecrireEtat({
        versionCible,
        versionBundleAuMomentDeLaTentative: VERSION_BUNDLE,
        tentatives,
        derniereTentativeLe: Date.now(),
    });

    console.log(`[version] Installation de la ${versionCible} (tentative ${tentatives}) - bundle courant ${VERSION_BUNDLE}`);

    await purgerEtRecharger(versionCible);
}

// ─────────────────────────────────────────────────────────────────────────────
// Boot
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Constate, au démarrage, le résultat de la tentative précédente.
 * Renvoie le nombre de tentatives infructueuses sur la version encore visée.
 */
async function bilanTentativePrecedente() {
    const etat = await lireEtat();
    if (!etat || !etat.versionCible) return 0;

    if (etat.versionCible === VERSION_BUNDLE) {
        // Le bundle exécuté est bien celui qui était visé : succès.
        console.log(`[version] Mise à jour ${VERSION_BUNDLE} appliquée avec succès`);
        await ecrireEtat({
            versionInstallee: VERSION_BUNDLE,
            versionCible: null,
            tentatives: 0,
            installeeLe: Date.now(),
        });
        return 0;
    }

    if (etat.tentatives > 0) {
        console.error(
            `[version] Mise à jour ${etat.versionCible} NON appliquée : le bundle exécuté est encore ` +
            `en ${VERSION_BUNDLE} après ${etat.tentatives} tentative(s). Service worker ou cache serveur bloqué.`
        );
    }
    return etat.tentatives || 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// API publique
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compare la version déployée à la version du bundle courant.
 * → { miseAJourDisponible, versionDeployee, versionBundle, buildTime }
 * miseAJourDisponible est false dès que le réseau ne répond pas : l'absence
 * d'information n'est pas une raison de bloquer l'utilisateur.
 */
async function verifierMiseAJour() {
    const infos = await lireVersionDeployee();
    derniereVerif = Date.now();

    if (!infos) {
        return { miseAJourDisponible: false, versionDeployee: null, versionBundle: VERSION_BUNDLE };
    }

    const disponible = infos.version !== VERSION_BUNDLE;

    if (disponible) {
        console.warn(`[version] Mise à jour disponible : ${VERSION_BUNDLE} → ${infos.version}`);
        // Trace de la dernière vérification réussie, utile au support.
        ecrireEtat({ versionBundle: VERSION_BUNDLE, derniereVerifLe: derniereVerif }).catch(() => {});
    } else {
        // Le déployé et l'exécuté concordent : plus aucune cible en cours. Sans
        // cette remise à zéro, une cible abandonnée (mise à jour retirée du
        // serveur, retour arrière) resterait en attente indéfiniment et ferait
        // journaliser un faux échec à chaque démarrage.
        ecrireEtat({
            versionBundle: VERSION_BUNDLE,
            versionInstallee: VERSION_BUNDLE,
            versionCible: null,
            tentatives: 0,
            derniereVerifLe: derniereVerif,
        }).catch(() => {});
    }

    return {
        miseAJourDisponible: disponible,
        versionDeployee: infos.version,
        versionBundle: VERSION_BUNDLE,
        buildTime: infos.buildTime || null,
    };
}

/**
 * Démarre la surveillance. `onMiseAJour(infos, nbEchecsPrecedents)` est appelé
 * dès qu'une divergence est constatée, et à chaque vérification tant qu'elle
 * persiste - la couche d'affichage est responsable de son idempotence.
 */
function demarrerSurveillance(onMiseAJour) {
    if (surveillanceDemarree) return;
    surveillanceDemarree = true;

    // Le nettoyage de l'URL est fait à l'import de appVersionUrl, bien plus tôt
    // dans le boot - il ne peut pas l'être ici, voir l'en-tête de ce module.

    let echecsPrecedents = 0;

    const verifier = async () => {
        if (verificationEnCours) return;
        verificationEnCours = true;
        try {
            const infos = await verifierMiseAJour();
            if (infos.miseAJourDisponible) {
                onMiseAJour(infos, echecsPrecedents);
            }
        } catch (e) {
            console.warn('[version] Vérification échouée', e);
        } finally {
            verificationEnCours = false;
        }
    };

    bilanTentativePrecedente()
        .then(n => { echecsPrecedents = n; })
        .catch(() => {})
        .then(verifier);

    setInterval(verifier, INTERVALLE_VERIF_MS);

    // Une caisse reste ouverte des journées entières sans rechargement : le
    // retour au premier plan est le moment le plus probable pour capter une
    // mise à jour déployée entre-temps.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' &&
            Date.now() - derniereVerif > DELAI_MIN_REVERIF_MS) {
            verifier();
        }
    });

    window.addEventListener('online', verifier);
}

export {
    VERSION_BUNDLE,
    verifierMiseAJour,
    appliquerMiseAJour,
    purgerEtRecharger,
    demarrerSurveillance,
    lireEtat,
};
